/**
 * The drawer's files, right side: where you are (source / folders), search, upload, the
 * folder's sub-folders as tiles and its files as cards (a picture shows itself), files of the
 * computer dropped anywhere on it kept there, and the ghost that follows a card being dragged
 * (onto the graph: a node attached to it; onto a folder: moved or copied there).
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import type { AssetInfo } from "@protocol/AssetInfo";
import type { AssetSource } from "@protocol/AssetSource";
import type { AssetPlace } from "../../state/assets";
import { useApp } from "../../app/context";
import { assetName, childFolders, countBelow, filesIn, formatSize, joinPath } from "../../shared/assets";
import { AssetThumb } from "../../shared/AssetThumb";
import { toast } from "../../shared/toast";
import { dragAsset } from "./assetDrag";
import type { FileTools } from "./fileTools";

/** What the gallery's host adds. */
export interface FileGalleryProps {
  /** The tools shared with the tree. */
  tools: FileTools;
  /** Buttons at the end of the header (dock or undock). */
  extra?: JSX.Element;
}

/** The files of the folder browsed. */
export function FileGallery(props: FileGalleryProps): JSX.Element {
  const { assets, menu } = useApp();
  const [query, setQuery] = createSignal("");
  const [dropping, setDropping] = createSignal(false);
  const [ghost, setGhost] = createSignal<{ asset: AssetInfo; x: number; y: number } | null>(null);
  const place = () => assets.place() ?? { source: "project" as const, folder: "" };
  const source = () => assets.source(place().source);
  const listing = () => source()?.listing ?? { source: place().source, folders: [], files: [] };
  const files = createMemo(() => filesIn(listing(), place().folder, query()));
  const folders = createMemo(() => (query() ? [] : childFolders(listing(), place().folder)));
  const crumbs = () => {
    const parts = place().folder ? place().folder.split("/") : [];
    return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join("/") }));
  };

  /** Keeps files of the computer in the folder browsed. */
  const upload = async (list: FileList | File[]) => {
    if (source()?.readOnly) return void toast(source()?.why ?? "", "error");
    const kept = await assets.upload(place().source, list, place().folder);
    if (kept.length) toast(kept.length === 1 ? `Kept ${kept[0]?.path}.` : `Kept ${kept.length} files.`);
  };
  /** A card dropped on a folder: moved there (same source) or copied (another source). */
  const toFolder = (asset: AssetInfo, from: AssetPlace, to: AssetSource, folder: string) => {
    if (to === from.source) {
      const target = joinPath(folder, assetName(asset.path));
      if (target !== asset.path) void assets.move(from.source, asset.path, target).then((ok) => ok && toast(`Moved ${assetName(asset.path)}.`));
    } else void assets.copy(from.source, asset.path, to, folder);
  };

  return (
    <div
      class="pgallery"
      classList={{ dropping: dropping() }}
      id="asset-gallery"
      data-asset-source={place().source}
      data-asset-folder={place().folder}
      onDragOver={(e) => {
        if (!e.dataTransfer?.types.includes("Files")) return;
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDropping(false)}
      onDrop={(e) => {
        setDropping(false);
        if (!e.dataTransfer?.files.length) return;
        e.preventDefault();
        void upload(e.dataTransfer.files);
      }}
    >
      <div class="dhead">
        <span class="pcrumbs" id="asset-crumbs">
          <button type="button" class="pcrumb" onClick={() => assets.go({ source: place().source, folder: "" })}>
            {source()?.label}
          </button>
          <For each={crumbs()}>
            {(c) => (
              <>
                <span class="pcrumb-sep">/</span>
                <button type="button" class="pcrumb" onClick={() => assets.go({ source: place().source, folder: c.path })}>
                  {c.name}
                </button>
              </>
            )}
          </For>
        </span>
        <input class="search" type="search" placeholder="Search files" aria-label="Filter files" id="asset-search" onInput={(e) => setQuery(e.currentTarget.value.trim())} />
        <span class="sp" />
        <Show when={!source()?.readOnly}>
          <button type="button" class="mini" id="new-asset-folder" onClick={() => props.tools.startNew(place().source, place().folder)}>
            New folder
          </button>
          <button type="button" class="btn primary" id="asset-upload-button" onClick={() => props.tools.pick(place().source, place().folder)}>
            Upload files
          </button>
        </Show>
        <input
          type="file"
          multiple
          hidden
          id="asset-upload"
          onChange={(e) => {
            const list = e.currentTarget.files;
            if (list?.length) void upload([...list]);
            e.currentTarget.value = "";
          }}
        />
        {props.extra}
      </div>
      <Show when={props.tools.naming()} keyed>
        {(naming) => (
          <div class="dhead asset-naming">
            <input
              class="field"
              id="asset-name"
              aria-label={naming.renaming === null ? "New folder's name" : "New name"}
              placeholder={naming.renaming === null ? "Folder name" : "Name"}
              value={naming.renaming === null ? "" : assetName(naming.renaming)}
              ref={(el) => queueMicrotask(() => (el.focus(), el.select()))}
              onKeyDown={(e) => {
                if (e.key === "Enter") void props.tools.finish(e.currentTarget.value);
                if (e.key === "Escape") props.tools.cancel();
                e.stopPropagation();
              }}
              onBlur={(e) => void props.tools.finish(e.currentTarget.value)}
            />
          </div>
        )}
      </Show>
      <div class="dbody" onContextMenu={(e) => e.target === e.currentTarget && menu.open(e, props.tools.folderMenu(place().source, place().folder))}>
        <Show when={source()?.readOnly}>
          <p class="pnote" id="asset-read-only">
            {source()?.why}
          </p>
        </Show>
        <div class="pgrid">
          <For each={folders()}>
            {(path) => (
              <button
                type="button"
                class="pfolder"
                data-asset-folder-tile={path}
                data-asset-drop-source={source()?.readOnly ? undefined : place().source}
                data-asset-drop-folder={path}
                onClick={() => assets.go({ source: place().source, folder: path })}
                onContextMenu={(e) => menu.open(e, props.tools.folderMenu(place().source, path))}
              >
                <span class="pfolder-icon" aria-hidden="true" />
                <span class="pname">{assetName(path)}</span>
                <span class="pmeta">{countBelow(listing(), path)} files</span>
              </button>
            )}
          </For>
          <For each={files()}>
            {(asset) => (
              <div class="pcard acard" data-asset={asset.path} data-asset-kind={asset.kind}>
                <button
                  type="button"
                  class="pmain"
                  title={`${asset.path} · ${formatSize(asset.size)}. Click to see it, drag it onto the graph to make a node of it.`}
                  onClick={() => assets.preview({ source: place().source, path: asset.path })}
                  onDblClick={() => void assets.download(place().source, asset.path)}
                  onPointerDown={(e) => {
                    const from = place();
                    dragAsset(e, from.source, asset, {
                      ghost: setGhost,
                      drop: (spot, page) => void props.tools.toGraph(from.source, asset, spot, page),
                      toFolder: (to, folder) => toFolder(asset, from, to, folder),
                    });
                  }}
                  onContextMenu={(e) => menu.open(e, props.tools.fileMenu(place().source, asset))}
                >
                  <AssetThumb asset={asset} url={asset.kind === "image" ? assets.url(asset.path, place().source) : null} />
                  <span class="pname">{assetName(asset.path)}</span>
                  <span class="pmeta">{formatSize(asset.size)}</span>
                </button>
              </div>
            )}
          </For>
          <Show when={!source()?.readOnly}>
            <button type="button" class="pnewcard" onClick={() => props.tools.pick(place().source, place().folder)}>
              <span>+</span>Upload files
            </button>
          </Show>
        </div>
        <Show when={!files().length && !folders().length}>
          <p class="pempty" id="asset-empty">
            {query() ? "No file matches here or below." : "No files here yet. Drop files of your computer here, or upload them: pictures, texts, sounds, anything up to 10 MB."}
          </p>
        </Show>
      </div>
      <Show when={ghost()}>
        {(g) => (
          <Portal>
            <div class="ghost" style={{ transform: `translate(${g().x + 12}px, ${g().y + 12}px)` }}>
              <span>{assetName(g().asset.path)}</span>
            </div>
          </Portal>
        )}
      </Show>
    </div>
  );
}
